// File: src/views/Gallery.js
// Summary:
// - Masonry (CSS columns) layout for artworks (no @mui/lab dependency)
// - If sold: show ONLY a red "Sold" chip; do NOT show price nor "On demand"
// - If available: show price (or "On demand"), an "Available" chip, and a CTA chip to buy (mailto);
//   clicking CTA does NOT open the fullscreen popin; hover is neutralized; icon forced white
// - Clicking artwork (outside CTA) opens a fullscreen, white slider popin
// - Fallback contact dialog appears if mailto is blocked
// - RESPONSIVE FULLSCREEN GALLERY

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Typography, Dialog, IconButton, Chip, Button, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';

const MAIL = 'hey@kajikaaki.com';

const Gallery = () => {
  const [artworks, setArtworks] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [fallbackTitle, setFallbackTitle] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/data/gallery.json');
        const data = await response.json();
        setArtworks(data);
      } catch (error) {
        console.error('Erreur lors du chargement des oeuvres', error);
      }
    };
    fetchData();
  }, []);

  const handleOpen = useCallback((idx) => {
    setSelectedIndex(idx);
    setSlideIndex(0);
  }, []);

  const handleClose = useCallback(() => {
    setSelectedIndex(null);
    setSlideIndex(0);
  }, []);

  const currentArtwork = useMemo(() => {
    if (selectedIndex === null) return null;
    return artworks[selectedIndex] || null;
  }, [selectedIndex, artworks]);

  const currentSlides = useMemo(() => {
    if (!currentArtwork) return [];
    const primary = currentArtwork.heavyImage || currentArtwork.mainImage;
    const others = Array.isArray(currentArtwork.gallery) ? currentArtwork.gallery : [];
    return [primary, ...others.filter((img) => img && img !== primary)];
  }, [currentArtwork]);

  const goPrev = useCallback(() => {
    setSlideIndex((i) => (i === 0 ? currentSlides.length - 1 : i - 1));
  }, [currentSlides.length]);

  const goNext = useCallback(() => {
    setSlideIndex((i) => (i === currentSlides.length - 1 ? 0 : i + 1));
  }, [currentSlides.length]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, goPrev, goNext, handleClose]);

  const formatPrice = (price) => {
    if (price === 0) return '0';
    if (price === null || price === undefined || String(price).trim() === '') return 'On demand';
    return String(price);
  };

  const openMail = (href) => {
    try {
      window.location.href = href;
    } catch (_) {}
    setTimeout(() => {
      setFallbackOpen(true);
    }, 900);
  };

  const handleEnquireClick = (e, art) => {
    e.stopPropagation(); // prevent opening the fullscreen popin
    const subject = encodeURIComponent(`Artwork enquiry: ${art.title}`);
    const body = encodeURIComponent(
      `Hello,\n\nI'm interested in this artwork: ${art.title}.\nCould you please share availability, price and shipping details?\n\nThanks!\n`
    );
    const href = `mailto:${MAIL}?subject=${subject}&body=${body}`;
    setFallbackTitle(art.title || '');
    openMail(href);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(MAIL);
    } catch (_) {}
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
      <Helmet>
        <title>KAJIKA AKI - Gallery</title>
      </Helmet>
      <Box>
        <Header />

        {/* Masonry via CSS columns (no @mui/lab) */}
        <Box sx={{ pt: { xs: 7, md: 10 }, pb: 10, px: { xs: 3, md: 10 } }}>
          <Box
            sx={{
              columnCount: { xs: 1, sm: 2, md: 3 },
              columnGap: (theme) => theme.spacing(4),
            }}
          >
            {artworks.map((art, idx) => {
              const isSold = !!art.sold;
              const priceLabel = formatPrice(art.price);

              return (
                <Box key={`${art.title}-${idx}`} sx={{ breakInside: 'avoid', mb: 4, cursor: 'pointer' }} onClick={() => handleOpen(idx)}>
                  <Box
                    component="img"
                    src={`/assets/images/${art.mainImage}`}
                    alt={art.title}
                    sx={{ width: '100%', display: 'block', borderRadius: 0 }}
                  />
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="h6" sx={{ fontFamily: 'Manrope, sans-serif', lineHeight: 1.2 }}>
                      {art.title}
                    </Typography>
                    {art.description && (
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                        {art.description}
                      </Typography>
                    )}
                    {art.size && (
                      <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                        {art.size}
                      </Typography>
                    )}

                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {!isSold && (
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {priceLabel}
                        </Typography>
                      )}

                      {isSold ? (
                        <Chip size="small" label="Sold" color="error" variant="filled" />
                      ) : (
                        <>
                          <Chip size="small" label="Available" variant="outlined" />
                          <Chip
                            size="small"
                            icon={<MailOutlineIcon sx={{ color: '#fff !important' }} />}
                            sx={{
                              backgroundColor: '#2231E9',
                              color: '#fff',
                              '&:hover': { backgroundColor: '#2231E9', opacity: 1 }, // neutralize hover
                            }}
                            label="Buy this artwork"
                            onClick={(e) => handleEnquireClick(e, art)}
                            clickable
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Fullscreen responsive gallery popin */}
      <Dialog 
        open={selectedIndex !== null} 
        onClose={handleClose} 
        fullScreen 
        PaperProps={{ 
          sx: { 
            backgroundColor: '#fff', 
            m: 0, 
            p: 0,
            // Ensure proper mobile viewport handling
            width: '100vw',
            height: '100vh',
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 0
          } 
        }}
      >
        {currentArtwork && (
          <Box 
            sx={{ 
              width: '100%', 
              height: '100%', 
              position: 'relative', 
              background: '#fff', 
              m: 0, 
              p: 0,
              overflow: 'hidden' // Prevent scrolling
            }}
          >
            {/* Close button - responsive positioning */}
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              sx={{ 
                position: 'absolute', 
                top: { xs: 12, md: 16 }, 
                right: { xs: 12, md: 16 }, 
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Navigation arrows - responsive positioning and sizing */}
            {currentSlides.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous"
                  onClick={goPrev}
                  sx={{ 
                    position: 'absolute', 
                    left: { xs: 8, md: 16 }, 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    },
                    // Smaller on mobile
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 }
                  }}
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                </IconButton>
                <IconButton
                  aria-label="Next"
                  onClick={goNext}
                  sx={{ 
                    position: 'absolute', 
                    right: { xs: 8, md: 16 }, 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    zIndex: 10,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)'
                    },
                    // Smaller on mobile
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 }
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                </IconButton>
              </>
            )}

            {/* Image container - responsive sizing */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                m: 0,
                p: { xs: 1, md: 2 }, // Small padding on mobile
                boxSizing: 'border-box'
              }}
            >
              {currentSlides.length > 0 && (
                <Box
                  component="img"
                  src={`/assets/images/${currentSlides[slideIndex]}`}
                  alt={currentArtwork.title}
                  sx={{
                    maxWidth: { xs: 'calc(100% - 16px)', md: '90%' },
                    maxHeight: { xs: 'calc(100% - 80px)', md: '80%' }, // Account for mobile navigation
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
              )}
            </Box>

            {/* Slide indicator for mobile */}
            {currentSlides.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: 16, md: 24 },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 2,
                  px: 2,
                  py: 1
                }}
              >
                <Typography variant="body2" sx={{ fontSize: { xs: 12, md: 14 } }}>
                  {slideIndex + 1} / {currentSlides.length}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Dialog>

      {/* Fallback enquiry dialog when mailto is blocked */}
      <Dialog 
        open={fallbackOpen} 
        onClose={() => setFallbackOpen(false)} 
        PaperProps={{ 
          sx: { 
            p: 0,
            m: { xs: 2, md: 4 }, // Responsive margins
            maxWidth: { xs: 'calc(100vw - 32px)', md: 520 },
            width: '100%'
          } 
        }}
      >
        <Box sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            Interested in this artwork?
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 3, fontSize: { xs: '0.9rem', md: '1rem' } }}>
            If your email app didn't open, just send us a message at <strong>{MAIL}</strong> and include the artwork title
            {fallbackTitle ? ` "${fallbackTitle}"` : ''}. We'll reply with availability, price and shipping details.
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 2, sm: 1 }} 
            sx={{ 
              mb: 0, 
              alignItems: { xs: 'stretch', sm: 'center' },
              '& .MuiChip-root': {
                alignSelf: { xs: 'flex-start', sm: 'center' }
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'center' } }}>
              <Chip 
                icon={<MailOutlineIcon />} 
                label={MAIL} 
                variant="outlined" 
                sx={{ 
                  maxWidth: { xs: '100%', sm: 'none' },
                  '& .MuiChip-label': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              />
            </Box>
            <Button 
              startIcon={<ContentCopyIcon />} 
              onClick={handleCopy}
              size="medium"
              fullWidth={{ xs: true, sm: false }}
              sx={{ minWidth: { xs: 'auto', sm: 120 } }}
            >
              Copy email
            </Button>
            <Button 
              variant="contained" 
              onClick={() => openMail(`mailto:${MAIL}?subject=${encodeURIComponent('Artwork enquiry')}`)}
              size="medium"
              fullWidth={{ xs: true, sm: false }}
              sx={{ minWidth: { xs: 'auto', sm: 140 } }}
            >
              Open email app
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </motion.div>
  );
};

export default Gallery;